import { useSupportContext } from "@/stores";
import { FeedbackType } from "@/types";
import { Close, Search } from "@mui/icons-material";
import {
  Box,
  capitalize,
  CircularProgress,
  Divider,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import debounce from "lodash.debounce";
import { FC, useEffect, useState } from "react";
import { useNotify } from "react-admin";
import InfiniteScroll from "react-infinite-scroll-component";
import { useGetFeedbacks } from "../../queries";
import { FeedbackItem } from "../feedback-item";

const NewFeedbacksList: FC = () => {
  const notify = useNotify();
  const { dispatch } = useSupportContext();

  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState(searchQuery);

  const {
    feedbacks,
    isFeedbacksLoading,
    feedbacksError,
    fetchNextFeedbackPage,
  } = useGetFeedbacks(debouncedSearchQuery);

  useEffect(() => {
    const handler = debounce(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 300);
    handler();

    return () => {
      handler.cancel();
    };
  }, [searchQuery]);

  useEffect(() => {
    if (feedbacksError) {
      notify("Error while fetching new feedbacks", { type: "error" });
    }
  }, [feedbacksError]);

  return (
    <Stack>
      <TextField
        label="Search by names"
        variant="outlined"
        value={searchQuery}
        sx={{ marginTop: 2 }}
        onChange={({ target: { value } }) => setSearchQuery(value)}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              {isFeedbacksLoading ? (
                <CircularProgress />
              ) : debouncedSearchQuery.length ? (
                <Close
                  sx={{ cursor: "pointer" }}
                  onClick={() => setSearchQuery("")}
                />
              ) : (
                <Search />
              )}
            </InputAdornment>
          ),
        }}
      />
      <Divider sx={{ marginTop: 2 }} />
      {isFeedbacksLoading && (
        <Box display="flex" justifyContent="center" pt={4}>
          <CircularProgress />
        </Box>
      )}
      {feedbacks?.meta.total === 0 && (
        <Box display="flex" justifyContent="center" pt={4}>
          <Typography variant="h4">No feedbacks found</Typography>
        </Box>
      )}
      {feedbacks && !isFeedbacksLoading && feedbacks?.meta.total !== 0 && (
        <InfiniteScroll
          pullDownToRefresh
          refreshFunction={() => null}
          height={"100%"}
          dataLength={feedbacks.meta.total}
          next={fetchNextFeedbackPage}
          hasMore={true}
          loader={<></>}
          style={{
            maxHeight: "100%",
            overflow: "auto",
            display: "flex",
            flexDirection: "column",
            paddingRight: 8,
          }}
        >
          {feedbacks.data.map(
            ({ id, sender, subject, type, body, createdAt }) => (
              <FeedbackItem
                key={id}
                body={body}
                time={createdAt}
                subTitle={subject}
                userId={sender.id}
                disabled={sender.isDeleted}
                imageSrc={sender.profileImage?.path ?? ""}
                title={
                  sender.isDeleted
                    ? "Deleted User"
                    : [sender.firstName, sender.lastName].join(" ")
                }
                tag={{
                  label: capitalize(type),
                  color:
                    type === FeedbackType.COMPLAINT ? "primary" : "secondary",
                }}
                onClick={() =>
                  dispatch({
                    type: "SET_CURRENT_CHAT",
                    payload: {
                      id,
                      isActive: false,
                      participant: sender,
                      feedback: {
                        id,
                        sender,
                        subject,
                        type,
                        body,
                        createdAt,
                      },
                    },
                  })
                }
              />
            )
          )}
        </InfiniteScroll>
      )}
    </Stack>
  );
};

export { NewFeedbacksList };
